import {
  LucideIcon,
  Briefcase,
  Megaphone,
  Building2,
  Calculator,
  Clock,
  AlertCircle,
  FileText,
  Target,
  TrendingUp,
  Users,
  Palette,
  FolderOpen,
  RefreshCw,
  Shield,
  FileCheck,
  Settings,
  Zap,
  CheckCircle,
  DollarSign,
  PieChart,
  Lock,
} from 'lucide-react'
import type { UiFragmentKind } from '@/data/platform'

export interface Feature {
  title: string
  description: string
  icon: LucideIcon
  outcomes: string[]
}

export interface CustomerStory {
  quote: string
  author: string
  title: string
  company: string
  beforeMetric: string
  afterMetric: string
  improvement: string
}

export interface ProblemPoint {
  title: string
  description: string
  icon: LucideIcon
}

/** Office app a diagram satellite tile represents (matches OfficeAppIcon). */
export type SolutionOfficeApp = 'word' | 'excel' | 'powerpoint' | 'outlook'

/** One pinned step on the solution page, with the tiles of its diagram. */
export interface SolutionStep {
  title: string
  description: string
  /** Satellite tiles around the team tile: an Office app or a data source. */
  nodes: Array<{ label: string; app?: SolutionOfficeApp }>
  /** Product UI fragment drawn for the step on layouts that use fragments. */
  fragment?: UiFragmentKind
}

/** A document type the team produces, drawn as a thumbnail. */
export interface SolutionDocument {
  kind: 'doc' | 'sheet' | 'slide' | 'mail'
  title: string
}

export interface SolutionFaq {
  question: string
  answer: string
}

/**
 * Page layout, one per solution so no two pages share a composition:
 * - insights: blue stats hero, pinned diagrams, rotated cards, scroll cards (sales)
 * - creative: white hero with a dock marquee, pink stats, sticky-left tiles, journey (marketing)
 * - works: split hero with a card tornado, statement, mirrored pinned steps, black grid (operations)
 * - statement: centred statement hero, 16:9 sheet, document strip, hover rows (finance)
 */
export type SolutionLayout = 'insights' | 'creative' | 'works' | 'statement'

export interface SolutionData {
  slug: 'sales' | 'marketing' | 'operations' | 'finance'
  layout: SolutionLayout
  icon: LucideIcon
  color: string
  colorDark: string

  // SEO
  metaTitle: string
  metaDescription: string

  // Hero Section
  teamLabel: string
  /** Short team name for the diagram's centre tile, e.g. "Sales". */
  teamName: string
  headline: string
  /** The headline split into the hero's two forced lines. */
  headlineLines: [string, string]
  /** Words of the headline set in highlight boxes (statement hero). */
  headlineHighlights?: string[]
  subheadline: string
  heroStats: Array<{ value: string; label: string }>

  // How it works (pinned steps), derived from the features below
  steps: SolutionStep[]
  /** Document types shown in the challenge band's thumbnail fan. */
  documents: SolutionDocument[]
  /** FAQ, answered from the copy on this page and the customer story. */
  faq: SolutionFaq[]
  /** Matching story id in data/customers.ts. */
  customerId: string

  // Problem Statement Section
  problemHeadline: string
  problemDescription: string
  problemPoints: ProblemPoint[]
  /** Phrases of the problem headline set in highlight boxes (statement layouts). */
  problemHighlights?: string[]

  // Feature Breakdown Section
  featuresHeadline: string
  featuresDescription: string
  features: Feature[]

  // Customer Story Section
  customerStory: CustomerStory

  // CTA Section
  ctaHeadline: string
  ctaDescription: string
}

export const solutionsData: Record<string, SolutionData> = {
  sales: {
    slug: 'sales',
    layout: 'insights',
    icon: Briefcase,
    color: 'bg-[#0f6cbd]',
    colorDark: 'dark:bg-[#479ef5]',

    metaTitle: 'Sales Solutions - Win More Deals Faster | Templatean',
    metaDescription: 'Respond to opportunities 3x faster with AI-generated proposals. Personalize every pitch automatically and stop losing deals to slower competitors.',

    teamLabel: 'For Sales Teams',
    teamName: 'Sales',
    headline: 'Win More Deals, Faster',
    headlineLines: ['Win More Deals,', 'Faster'],
    subheadline: 'Respond to opportunities 3x faster with AI-generated proposals. Personalize every pitch automatically. Stop losing deals to slower competitors.',
    heroStats: [
      { value: '3x', label: 'Faster proposal turnaround' },
      { value: '+15-25%', label: 'Win rate improvement' },
      { value: '0', label: 'Pricing errors' },
    ],

    steps: [
      {
        title: 'Pull the deal from your CRM',
        description: 'Prospect data comes straight from HubSpot, or from your own systems through a database or REST connector, so every proposal is personalized with zero manual entry.',
        nodes: [{ label: 'CRM' }, { label: 'Word', app: 'word' }],
      },
      {
        title: 'Draft the proposal in minutes',
        description: 'AI generates a complete proposal from your templates and content library, personalized to each prospect.',
        nodes: [{ label: 'Templates' }, { label: 'Word', app: 'word' }, { label: 'PowerPoint', app: 'powerpoint' }],
      },
      {
        title: 'Price it without the errors',
        description: 'Pricing tables pull from your product catalog and calculate automatically. No copy-paste, no outdated quotes.',
        nodes: [{ label: 'Catalog' }, { label: 'Excel', app: 'excel' }, { label: 'Outlook', app: 'outlook' }],
      },
    ],
    documents: [
      { kind: 'doc', title: 'Proposal' },
      { kind: 'sheet', title: 'Pricing' },
      { kind: 'doc', title: 'RFP response' },
      { kind: 'slide', title: 'Sales deck' },
    ],
    faq: [
      {
        question: 'How fast can we turn around a proposal?',
        answer: 'Same day. AI generates complete proposals in minutes from your templates and content library. Greenfield Consulting went from five-day proposals to same-day delivery.',
      },
      {
        question: 'Does it work with our CRM?',
        answer: 'Templatean connects to HubSpot and pulls prospect data straight into your proposals, with zero manual entry. Your own systems connect through a SQL database or a custom REST API connector.',
      },
      {
        question: 'How do you prevent pricing errors?',
        answer: 'Smart pricing tables pull from your product catalog and calculate automatically. No more copy-paste errors or outdated quotes.',
      },
      {
        question: 'Can it help with RFPs?',
        answer: 'AI analyzes RFP requirements and suggests content from your knowledge base, so you respond faster and more accurately.',
      },
      {
        question: 'Is there a free trial?',
        answer: 'Yes. A 14-day free trial, no credit card required.',
      },
    ],
    customerId: 'greenfield-consulting',

    problemHeadline: 'Every hour spent on proposals is an hour not spent selling',
    problemDescription: 'Your sales team is drowning in document work. RFP responses take days. Proposals have inconsistent branding. Pricing errors slip through. Meanwhile, competitors are responding faster and winning deals that should be yours.',
    problemPoints: [
      {
        title: 'Slow Response Times',
        description: 'Multi-day turnaround on proposals means lost opportunities',
        icon: Clock,
      },
      {
        title: 'Inconsistent Quality',
        description: 'Every rep creates proposals differently, diluting your brand',
        icon: AlertCircle,
      },
      {
        title: 'Manual Pricing',
        description: 'Copy-paste pricing leads to costly errors',
        icon: Calculator,
      },
    ],

    featuresHeadline: 'Everything sales teams need to close faster',
    featuresDescription: 'From first outreach to signed contract, Templatean accelerates every document in your sales process.',
    features: [
      {
        title: 'AI Proposal Generation',
        description: 'Generate complete proposals in minutes, personalized to each prospect using your templates and content library.',
        icon: FileText,
        outcomes: ['Same-day proposal delivery', 'Consistent brand messaging', 'Personalized at scale'],
      },
      {
        title: 'Smart Pricing Tables',
        description: 'Dynamic pricing that pulls from your product catalog. No more copy-paste errors or outdated quotes.',
        icon: DollarSign,
        outcomes: ['Zero pricing errors', 'Real-time product data', 'Automatic calculations'],
      },
      {
        title: 'RFP Response Automation',
        description: 'AI analyzes RFP requirements and suggests content from your knowledge base. Respond faster and more accurately.',
        icon: Target,
        outcomes: ['80% faster RFP responses', 'Higher compliance scores', 'Reusable content library'],
      },
      {
        title: 'CRM Integration',
        description: 'Pull prospect data directly from HubSpot, or from your own systems through a database or REST connector. Proposals are auto-personalized with zero manual entry.',
        icon: Users,
        outcomes: ['One-click personalization', 'Accurate contact info', 'Activity tracking'],
      },
    ],

    customerStory: {
      quote: "We used to lose deals because we couldn't turn proposals around fast enough. Now we respond the same day—and our proposals look better than ever.",
      author: 'Sarah Mitchell',
      title: 'Operations Director',
      company: 'Greenfield Consulting',
      beforeMetric: '5 days',
      afterMetric: 'Same day',
      improvement: 'Proposal turnaround',
    },

    ctaHeadline: 'Ready to accelerate your sales cycle?',
    ctaDescription: 'See how Templatean helps sales teams respond faster and win more deals. 14-day free trial, no credit card required.',
  },

  marketing: {
    slug: 'marketing',
    layout: 'creative',
    icon: Megaphone,
    color: 'bg-[#0e7a0d]',
    colorDark: 'dark:bg-[#54b054]',

    metaTitle: 'Marketing Solutions - Protect Your Brand Without Bottlenecks | Templatean',
    metaDescription: 'Achieve 98% brand compliance automatically. One source of truth for every asset. Empower everyone without losing control.',

    teamLabel: 'For Marketing & Brand',
    teamName: 'Marketing',
    headline: 'Protect Your Brand Without Bottlenecks',
    headlineLines: ['Protect Your Brand', 'Without Bottlenecks'],
    subheadline: 'Achieve 98% brand compliance automatically. One source of truth for every asset. Empower everyone without losing control.',
    heroStats: [
      { value: '98%', label: 'Brand compliance' },
      { value: '90%', label: 'Fewer asset requests' },
      { value: '1', label: 'Source of truth' },
    ],

    steps: [
      {
        title: 'One library for every asset',
        description: 'Logos, colors, fonts and templates live in one place. Always current, always accessible.',
        nodes: [{ label: 'Logos' }, { label: 'Fonts' }, { label: 'PowerPoint', app: 'powerpoint' }],
      },
      {
        title: 'Lock what matters',
        description: 'Allow customization where it helps and lock critical brand elements everywhere else. Flexibility with guardrails.',
        nodes: [{ label: 'Locked' }, { label: 'Word', app: 'word' }],
      },
      {
        title: 'Update once, current everywhere',
        description: 'Change a template once and every team gets the new version. Usage analytics show which assets get used.',
        nodes: [{ label: 'Word', app: 'word' }, { label: 'Excel', app: 'excel' }, { label: 'Outlook', app: 'outlook' }],
      },
    ],
    documents: [
      { kind: 'doc', title: 'Brand template' },
      { kind: 'slide', title: 'Sales deck' },
      { kind: 'sheet', title: 'Usage report' },
      { kind: 'mail', title: 'Campaign email' },
    ],
    faq: [
      {
        question: 'How do we stop old logos from circulating?',
        answer: 'Every logo, color, font and template lives in one brand asset library with automatic version control. TechStart Solutions went from 12 logo versions in circulation to one.',
      },
      {
        question: 'Can teams customize templates without breaking the brand?',
        answer: 'Yes. Template lock controls allow customization where appropriate while locking critical brand elements.',
      },
      {
        question: 'What happens when we update a template?',
        answer: "Update it once and it's current everywhere. No more hunting down old versions.",
      },
      {
        question: 'Can we see which assets get used?',
        answer: 'Usage analytics show which templates and assets are used most, so you can spot adoption gaps and optimization opportunities.',
      },
      {
        question: 'Is there a free trial?',
        answer: 'Yes. A 14-day free trial, no credit card required.',
      },
    ],
    customerId: 'techstart-solutions',

    problemHeadline: "Your brand is your most valuable asset—but it's under constant threat",
    problemDescription: "Every department needs branded content, but marketing can't review everything. Outdated logos appear in presentations. Colors drift. Messaging goes off-script. You're either a bottleneck or watching your brand erode.",
    problemPoints: [
      {
        title: 'Asset Sprawl',
        description: '12 versions of your logo floating around, none of them current',
        icon: FolderOpen,
      },
      {
        title: 'Request Overload',
        description: 'Constant interruptions for assets that should be self-service',
        icon: RefreshCw,
      },
      {
        title: 'Inconsistent Output',
        description: "Teams creating content that doesn't match brand guidelines",
        icon: Palette,
      },
    ],

    featuresHeadline: 'Brand management that scales with your organization',
    featuresDescription: 'Give every team access to create on-brand content while marketing maintains control.',
    features: [
      {
        title: 'Brand Asset Library',
        description: 'Centralized repository for logos, colors, fonts, and templates. Always current, always accessible.',
        icon: FolderOpen,
        outcomes: ['Single source of truth', 'Automatic version control', 'Easy asset discovery'],
      },
      {
        title: 'Template Lock Controls',
        description: 'Allow customization where appropriate while locking critical brand elements. Flexibility with guardrails.',
        icon: Lock,
        outcomes: ['Protected brand elements', 'Controlled customization', 'No rogue designs'],
      },
      {
        title: 'Usage Analytics',
        description: 'See which templates and assets are used most. Identify adoption gaps and optimization opportunities.',
        icon: PieChart,
        outcomes: ['Asset usage insights', 'Template performance', 'Adoption tracking'],
      },
      {
        title: 'Instant Updates',
        description: "Update a template once and it's current everywhere. No more hunting down old versions.",
        icon: Zap,
        outcomes: ['Real-time distribution', 'No version confusion', 'Consistent messaging'],
      },
    ],

    customerStory: {
      quote: "Our brand consistency went from a constant headache to something I don't even think about anymore. It just works.",
      author: 'Elena Rodriguez',
      title: 'Marketing Manager',
      company: 'TechStart Solutions',
      beforeMetric: '45%',
      afterMetric: '98%',
      improvement: 'Brand compliance',
    },

    ctaHeadline: 'Ready to scale your brand without scaling your team?',
    ctaDescription: 'See how Templatean helps marketing teams maintain brand consistency across the entire organization.',
  },

  operations: {
    slug: 'operations',
    layout: 'works',
    icon: Building2,
    color: 'bg-[#9d5d00]',
    colorDark: 'dark:bg-[#d08700]',

    metaTitle: 'Operations Solutions - Eliminate the Paperwork | Templatean',
    metaDescription: 'Generate HR documents in minutes, not hours. Deploy policy updates instantly. Pass audits with zero findings.',

    teamLabel: 'For Operations & Admin',
    teamName: 'Operations',
    headline: 'Eliminate the Paperwork',
    headlineLines: ['Eliminate the', 'Paperwork'],
    subheadline: 'Generate HR documents in minutes, not hours. Deploy policy updates instantly. Pass audits with zero findings.',
    heroStats: [
      { value: '90%', label: 'Faster document creation' },
      { value: '0', label: 'Version control issues' },
      { value: 'Days', label: 'Audit prep (was weeks)' },
    ],

    steps: [
      {
        title: 'Generate HR documents in seconds',
        description: 'Offer letters, contracts and onboarding packets are generated from employee data, error-free.',
        nodes: [{ label: 'HRIS' }, { label: 'Word', app: 'word' }],
        fragment: 'form',
      },
      {
        title: 'Push policy updates everywhere',
        description: 'Update a policy once and push it everywhere. Acknowledgments are tracked automatically.',
        nodes: [{ label: 'Policy' }, { label: 'Outlook', app: 'outlook' }, { label: 'Word', app: 'word' }],
        fragment: 'mail',
      },
      {
        title: 'Stay audit-ready',
        description: 'Every document carries a complete change history and access log, and approvals route automatically.',
        nodes: [{ label: 'Audit log' }, { label: 'Excel', app: 'excel' }],
        fragment: 'audit',
      },
    ],
    documents: [
      { kind: 'doc', title: 'Offer letter' },
      { kind: 'mail', title: 'Policy update' },
      { kind: 'doc', title: 'Onboarding' },
      { kind: 'sheet', title: 'Audit log' },
    ],
    faq: [
      {
        question: 'Which HR documents can we generate?',
        answer: 'Offer letters, contracts and onboarding packets, all generated from employee data in seconds.',
      },
      {
        question: 'How do policy updates reach everyone?',
        answer: 'Update a policy once and push it everywhere. Acknowledgments are tracked automatically, with full version history.',
      },
      {
        question: 'What does audit prep look like?',
        answer: 'Generate audit-ready documentation with complete change history and access logs. Audit prep takes days instead of weeks.',
      },
      {
        question: 'Can approvals be automated?',
        answer: 'Yes. Documents route for approval automatically, so nobody chases signatures or wonders about status.',
      },
      {
        question: 'Is there a free trial?',
        answer: 'Yes. A 14-day free trial, no credit card required.',
      },
    ],
    customerId: 'pacific-manufacturing',

    problemHeadline: 'Operational excellence is buried under administrative burden',
    problemHighlights: ['excellence', 'administrative burden'],
    problemDescription: "Your team spends more time managing documents than improving processes. HR paperwork piles up. Policy updates take weeks to distribute. Audits mean scrambling through folders to prove compliance. There's a better way.",
    problemPoints: [
      {
        title: 'Document Chaos',
        description: 'Critical documents scattered across drives and email chains',
        icon: FolderOpen,
      },
      {
        title: 'Slow Distribution',
        description: 'Policy updates take weeks to reach the right people',
        icon: Clock,
      },
      {
        title: 'Audit Anxiety',
        description: "Can you prove who saw what, and when? Most teams can't",
        icon: FileCheck,
      },
    ],

    featuresHeadline: 'Streamline operations with intelligent document automation',
    featuresDescription: 'From HR onboarding to compliance documentation, Templatean handles the paperwork so you can focus on people.',
    features: [
      {
        title: 'HR Document Generation',
        description: 'Offer letters, contracts, onboarding packets—all generated from employee data in seconds.',
        icon: FileText,
        outcomes: ['Minutes instead of hours', 'Error-free documents', 'Consistent experience'],
      },
      {
        title: 'Policy Management',
        description: 'Update a policy once and push it everywhere. Track acknowledgments automatically.',
        icon: Settings,
        outcomes: ['Instant distribution', 'Acknowledgment tracking', 'Version history'],
      },
      {
        title: 'Compliance Documentation',
        description: 'Generate audit-ready documentation with complete change history and access logs.',
        icon: Shield,
        outcomes: ['Full audit trails', 'Access logging', 'Automated reporting'],
      },
      {
        title: 'Workflow Automation',
        description: 'Route documents for approval automatically. No more chasing signatures or wondering about status.',
        icon: TrendingUp,
        outcomes: ['Automatic routing', 'Status visibility', 'Faster approvals'],
      },
    ],

    customerStory: {
      quote: "Audit prep used to mean weeks of document gathering. Now we pull everything in minutes. Our last audit had zero findings.",
      author: 'Michael Chen',
      title: 'Director of Operations',
      company: 'Pacific Manufacturing',
      beforeMetric: 'Weeks',
      afterMetric: 'Days',
      improvement: 'Audit preparation time',
    },

    ctaHeadline: 'Ready to eliminate operational paperwork?',
    ctaDescription: 'See how Templatean helps operations teams automate document creation and maintain compliance effortlessly.',
  },

  finance: {
    slug: 'finance',
    layout: 'statement',
    icon: Calculator,
    color: 'bg-[#5c2d91]',
    colorDark: 'dark:bg-[#8764b8]',

    metaTitle: 'Finance Solutions - Models You Can Trust | Templatean',
    metaDescription: 'Build financial models in hours, not days. AI that shows every formula. Catch errors before they matter.',

    teamLabel: 'For Finance & Accounting',
    teamName: 'Finance',
    headline: 'Models You Can Trust',
    headlineLines: ['Models You', 'Can Trust'],
    headlineHighlights: ['Models', 'Trust'],
    subheadline: 'Build financial models in hours, not days. AI that shows every formula. Catch errors before they matter.',
    heroStats: [
      { value: '60%', label: 'Faster model builds' },
      { value: '-95%', label: 'Formula errors' },
      { value: '100%', label: 'Audit trails' },
    ],

    steps: [
      {
        title: 'Describe it, get the formula',
        description: 'Ask in plain English. AI suggests the right formula and explains every component.',
        nodes: [{ label: 'Formula' }, { label: 'Excel', app: 'excel' }],
      },
      {
        title: 'Catch errors before they matter',
        description: 'AI continuously checks your models for common errors, inconsistencies and range issues.',
        nodes: [{ label: 'Checks' }, { label: 'Excel', app: 'excel' }, { label: 'Word', app: 'word' }],
      },
      {
        title: 'Document and audit everything',
        description: 'Model logic, assumptions and data sources are documented automatically, and every change is logged with who, when and why.',
        nodes: [{ label: 'Audit trail' }, { label: 'Word', app: 'word' }, { label: 'PowerPoint', app: 'powerpoint' }],
      },
    ],
    documents: [
      { kind: 'sheet', title: 'Financial model' },
      { kind: 'sheet', title: 'Budget' },
      { kind: 'slide', title: 'Board deck' },
      { kind: 'doc', title: 'Model notes' },
    ],
    faq: [
      {
        question: 'Does the AI write formulas for us?',
        answer: 'Describe what you need in plain English. AI suggests the right formula and explains every component.',
      },
      {
        question: 'How are errors caught?',
        answer: 'AI continuously analyzes your models for common errors, inconsistencies and potential issues, in real time.',
      },
      {
        question: 'Can auditors see how a number was built?',
        answer: 'Every change is logged with who, when and why, and model documentation explains logic, assumptions and data sources. Restore any previous version instantly.',
      },
      {
        question: 'How much faster are model builds?',
        answer: 'Teams build models in hours, not days. Summit Advisory Group cut model build time from 3–4 days to 4–6 hours.',
      },
      {
        question: 'Is there a free trial?',
        answer: 'Yes. A 14-day free trial, no credit card required.',
      },
    ],
    customerId: 'summit-advisory',

    problemHeadline: 'Financial accuracy demands rigor—but spreadsheet chaos undermines trust',
    problemDescription: 'Critical decisions rest on spreadsheets that nobody fully understands. Formula errors hide in plain sight. Version confusion leads to bad data. Auditors ask questions you cannot answer with confidence.',
    problemPoints: [
      {
        title: 'Hidden Errors',
        description: 'Spreadsheet mistakes that cost millions—discovered too late',
        icon: AlertCircle,
      },
      {
        title: 'Black Box Models',
        description: 'Complex formulas that only one person understands',
        icon: Calculator,
      },
      {
        title: 'Audit Uncertainty',
        description: "Can you explain every number's source? Every change?",
        icon: FileCheck,
      },
    ],

    featuresHeadline: 'Financial intelligence that builds trust',
    featuresDescription: 'From budgets to board decks, Templatean brings transparency and accuracy to financial document creation.',
    features: [
      {
        title: 'AI Formula Assistant',
        description: 'Describe what you need in plain English. AI suggests the right formula and explains every component.',
        icon: Zap,
        outcomes: ['Natural language input', 'Formula explanations', 'Learning suggestions'],
      },
      {
        title: 'Error Detection',
        description: 'AI continuously analyzes your models for common errors, inconsistencies, and potential issues.',
        icon: CheckCircle,
        outcomes: ['Real-time error catching', 'Consistency checks', 'Range validation'],
      },
      {
        title: 'Model Documentation',
        description: 'Auto-generate documentation that explains model logic, assumptions, and data sources.',
        icon: FileText,
        outcomes: ['Automatic documentation', 'Assumption tracking', 'Data lineage'],
      },
      {
        title: 'Complete Audit Trail',
        description: 'Every change logged with who, when, and why. Restore any previous version instantly.',
        icon: Shield,
        outcomes: ['Full change history', 'Point-in-time recovery', 'Compliance ready'],
      },
    ],

    customerStory: {
      quote: "The financial models our team builds now are more accurate and take half the time. I sleep better knowing the numbers are right.",
      author: 'David Nguyen',
      title: 'CFO',
      company: 'Summit Advisory Group',
      beforeMetric: 'Days',
      afterMetric: 'Hours',
      improvement: 'Model build time',
    },

    ctaHeadline: 'Ready to build financial models you can trust?',
    ctaDescription: 'See how Templatean helps finance teams create accurate, transparent, and auditable financial documents.',
  },
}

export const solutionsList = Object.values(solutionsData)
